import type {Hotel} from '@/redux/v1/hotels/list/models';

import {useFirstMountState, usePrevious} from '@react-hookz/web';
import {Flex} from '@travel-frontend/ui-components';
import {pick} from 'lodash';
import {forwardRef, Fragment, memo, useCallback, useEffect} from 'react';
import {useLocation} from 'react-router';

import {NoOffer} from '@/modules/common/components/NoOffer';
import {FILTER_PARAMS_HOTELS} from '@/modules/common/context/QueryParams/models';
import {useLocalSplit, useUpliftAds} from '@/modules/common/hooks';
import {useSponsoredAdsSplit} from '@/modules/common/hooks/splits/useSponsoredAdsSplit';
import {useFilterParams} from '@/modules/common/hooks/useFilterParams';
import {getHotelDetailsFetchMethod} from '@/modules/common/hooks/useHotelDetails/utils';
import {useLinkBuilder} from '@/modules/common/hooks/useLinkBuilder';
import {useSearchParams} from '@/modules/common/hooks/useSearchParams';
import {useServiceCenterBanner} from '@/modules/common/hooks/useServiceCenterBanner';
import {useSortParams} from '@/modules/common/hooks/useSortParams';
import {useTracking} from '@/modules/common/hooks/useTracking';
import {Banners} from '@/modules/hotels/components/List/components/Banners';
import {Footer} from '@/modules/hotels/components/List/components/Footer';
import {
  HotelBox,
  type Props as HotelBoxProps,
} from '@/modules/hotels/components/List/components/HotelBox';
import {HotelBoxPlaceholder} from '@/modules/hotels/components/List/components/HotelBoxPlaceholder';
import {Wrapper} from '@/modules/hotels/components/List/components/Wrapper';
import {useFilters} from '@/modules/hotels/hooks/useFilters';
import {queryClient} from '@/queryClient';
import {useAppDispatch, useAppSelector} from '@/redux/config';
import {selectHotelItemEvent} from '@/redux/middleware/googleTagManager';
import {hotelListLoadedEvent} from '@/redux/middleware/googleTagManager/events/hotelListLoaded';
import {fetchHotelsAndInventory} from '@/redux/v1/hotels/actions';
import {fetchNextHotels} from '@/redux/v1/hotels/list/actions';
import {
  areHotelsLoaded as getAreHotelsLoaded,
  getDuplicateCount,
  getHotelCountFiltered,
  getHotelCountUnfiltered,
  haveHotelsFailedLoading as getHaveHotelsFailedLoading,
  selectAreHotelsLoading,
  selectPage,
  selectPageSize,
  selectPaginatedHotels,
} from '@/redux/v1/hotels/list/reducer';
import {chunkWithIndex} from '@/utils/chunkWithIndex';

type Props = {
  visuallyHidden: boolean;
  mapViewActive?: boolean;
  pinnedHotels?: any;
  onHover?: (hovered: string | null) => void;
  hovered?: string | null;
  small?: boolean;
  splitView?: boolean;
};

const ListComponent = forwardRef<HTMLDivElement, Props>(function List(
  {
    visuallyHidden,
    mapViewActive,
    pinnedHotels,
    onHover,
    hovered,
    small,
    splitView,
    ...restProps
  },
  ref
) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const isFirstMount = useFirstMountState();
  const {dispatchGenericTrackingEvent} = useTracking();
  // @TODO change composition and try to move NoOffers rendering outside into the Layout
  const {hasFilters, clear: clearFilters} = useFilters();
  const {buildOfferListLink} = useLinkBuilder();
  const {sponsoredAdEnabled, sponsoredAdPositions} = useSponsoredAdsSplit();
  const firstUpliftAd = useUpliftAds(
    import.meta.env.VITE_UPLIFT_ADS_FIRST_INLINE_ZONE_ID
  );
  const secondUpliftAd = useUpliftAds(
    import.meta.env.VITE_UPLIFT_ADS_SECOND_INLINE_ZONE_ID
  );

  const [searchParams] = useSearchParams();
  const [filterParams] = useFilterParams();
  const [sortParams] = useSortParams();
  const previousSearchParams = usePrevious(searchParams);
  const previousFilterParams = usePrevious(filterParams);
  const previousSortParams = usePrevious(sortParams);

  const isHotelContentApiFeatureOn =
    useLocalSplit<'default' | 'off' | 'on'>('FFP_5733_hotel_content_api')
      .treatment === 'on';

  const selectedPaginatedHotels = useAppSelector(selectPaginatedHotels);
  const areHotelsLoading = useAppSelector(selectAreHotelsLoading);
  const areHotelsLoaded = useAppSelector(getAreHotelsLoaded);
  const haveHotelsFailedLoading = useAppSelector(getHaveHotelsFailedLoading);
  const hotelCountUnfiltered = useAppSelector(getHotelCountUnfiltered);
  const hotelCountFiltered = useAppSelector(getHotelCountFiltered);
  const currentPage = useAppSelector(selectPage);
  const previousPage = usePrevious(currentPage);
  const pageSize = useAppSelector(selectPageSize);
  const duplicateCount = useAppSelector(getDuplicateCount);

  const changedBy = location.state?.changedBy;
  const paginatedHotels: Array<Hotel> = pinnedHotels ?? selectedPaginatedHotels;

  const showPlaceholders = areHotelsLoading;
  // error on initial request or on later requests
  const showNoOffer =
    haveHotelsFailedLoading ||
    (areHotelsLoaded &&
      (hotelCountUnfiltered === 0 || hotelCountFiltered === 0)) ||
    (areHotelsLoading && paginatedHotels.length === 0 && mapViewActive);

  // Show service center banner only if hotel list is not empty
  const serviceCenterBannerData = useServiceCenterBanner({
    page: 'hotels',
    enabled: !showNoOffer,
  });

  // #region ACTIONS

  const loadMore = () => {
    dispatchGenericTrackingEvent({
      event: 'click',
      event_action: 'load_more_hotels',
      list_position: paginatedHotels.length,
      non_interaction: false,
    });

    dispatch(
      fetchNextHotels({
        filterParams,
        sortParams,
        searchParams,
      })
    );
  };

  const trackHotelListLoaded = async () => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    const lazy = previousPage != null && currentPage > previousPage;
    const hotelsToTrack = paginatedHotels.slice(start, end);
    const hotelDetailsQueryOptionsMethod = getHotelDetailsFetchMethod({
      isHotelContentApiFeatureOn,
    });
    // we can't track everything at once, otherwise we'd reach some Google Analytics limit
    for (const hotels of chunkWithIndex(hotelsToTrack, 25)) {
      try {
        // we must ensure that all hotel details are fetched before tracking
        const results = await Promise.allSettled(
          hotels.map(async ([position, hotel]) => ({
            position: start + position,
            offer: hotel,
            data: await queryClient.ensureQueryData(
              // @ts-expect-error TS2345
              hotelDetailsQueryOptionsMethod(hotel.id)
            ),
          }))
        );
        dispatchGenericTrackingEvent(
          hotelListLoadedEvent({
            lazy,
            destinationId: searchParams.destinationId ?? '',
            filterParams,
            sortParams,
            hotelsCount: hotelCountFiltered,
            origin: changedBy,
            hotels: results.map((result) => {
              return result.status === 'fulfilled'
                ? {
                    position: result.value.position,
                    offer: result.value.offer,
                    data: result.value.data,
                  }
                : {
                    position: -1,
                    offer: undefined,
                    data: undefined,
                  };
            }),
          })
        );
      } catch {
        // ignore
      }
    }
  };

  // #region EVENT HANDLERS

  const handleHotelBoxMouseEnter = useCallback(
    (hotelId: string) => {
      if (onHover) {
        onHover(hotelId);
      }
    },
    [onHover]
  );

  const handleHotelBoxMouseLeave = useCallback(() => {
    if (onHover) {
      onHover(null);
    }
  }, [onHover]);

  const handleSelectHotel = useCallback<NonNullable<HotelBoxProps['onSelect']>>(
    ({hotelId, origin, position}) => {
      dispatch(
        selectHotelItemEvent.action({
          hotelId,
          position,
          isMap: false,
          isGallery: origin === 'pictureGalleryOverlay',
          sortParams,
          isHotelContentApiFeatureOn,
        })
      );
    },
    [dispatch, sortParams]
  );

  // #region EFFECTS

  /**
   * @TODO This NEEDS to be refactored! Fetch in event handlers instead (including routing)
   */
  useEffect(() => {
    // skip fetching hotels when returning back from offerList on mobile
    if (isFirstMount && areHotelsLoaded && !location?.state?.forceReload) {
      return;
    }

    const searchParamsChanged = searchParams !== previousSearchParams;
    const filterParamsChanged = filterParams !== previousFilterParams;
    const sortParamsChanged = sortParams !== previousSortParams;

    // all params will have "changed" on initial render
    if (!searchParamsChanged && !filterParamsChanged && !sortParamsChanged) {
      return;
    }

    const initial =
      // available filter options depend on searchParams, so we need to update whats available
      searchParamsChanged ||
      // first mount is always initial, subsequent ones are updates
      isFirstMount;

    dispatch(
      fetchHotelsAndInventory({
        initial,
        searchParams,
        filterParams: pick(filterParams, FILTER_PARAMS_HOTELS),
        sortParams,
        // @FIXME terrible workaround
        forceNoLimit: mapViewActive,
        sponsoredAdPositions: sponsoredAdEnabled
          ? sponsoredAdPositions
          : undefined,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- todo
  }, [
    areHotelsLoaded,
    isFirstMount,
    searchParams,
    previousSearchParams,
    filterParams,
    previousFilterParams,
    sortParams,
    previousSortParams,
    dispatch,
  ]);

  useEffect(() => {
    if (showPlaceholders || showNoOffer || splitView) {
      return;
    }
    trackHotelListLoaded();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally ignoring "trackHotelListLoaded" here. The underlying issue is the useEffect itself.
  }, [showPlaceholders, showNoOffer, splitView, currentPage]);

  useEffect(() => {
    if (duplicateCount.before !== duplicateCount.now) {
      dispatchGenericTrackingEvent({
        event: 'find',
        event_action: 'duplicate_hotel_count',
        duplicate_hotel_count: duplicateCount.now - duplicateCount.before,
        non_interaction: true,
      });
    }
  }, [duplicateCount.before, duplicateCount.now, dispatchGenericTrackingEvent]);

  useEffect(() => {
    if (
      (areHotelsLoaded || haveHotelsFailedLoading) &&
      (hotelCountFiltered === 0 || hotelCountUnfiltered === 0)
    ) {
      dispatchGenericTrackingEvent({event: 'nooffer', event_action: 'nooffer'});
    }
  }, [
    areHotelsLoaded,
    dispatchGenericTrackingEvent,
    haveHotelsFailedLoading,
    hotelCountFiltered,
    hotelCountUnfiltered,
  ]);

  // #region RENDER

  const topZoneAd = useUpliftAds(
    import.meta.env.VITE_UPLIFT_ADS_NATIVE_TOP_ZONE_ID
  );

  return (
    <Flex
      ref={ref}
      {...restProps}
      spacing="pages/small"
      display={visuallyHidden ? 'none' : undefined}
    >
      {(() => {
        if (showPlaceholders) {
          return (
            <>
              {Array.from({length: pageSize}, (_, index) => (
                <HotelBoxPlaceholder key={index} small={small} />
              ))}
            </>
          );
        }

        if (showNoOffer) {
          return (
            <NoOffer
              hasFilters={hasFilters}
              small={small}
              onClickClearFiltersButton={clearFilters}
            />
          );
        }

        return (
          <>
            {paginatedHotels.map((hotel: Hotel, index: number) => {
              const link = buildOfferListLink({
                hotelId: hotel.id,
                getSortParams: () => null,
              });

              return (
                <Fragment key={hotel.id}>
                  <Wrapper
                    isHovered={hovered === hotel.id}
                    onMouseEnter={() => handleHotelBoxMouseEnter(hotel.id)}
                    onMouseLeave={handleHotelBoxMouseLeave}
                  >
                    <HotelBox
                      position={index}
                      key={hotel.id}
                      link={link}
                      hotelId={hotel.id}
                      offer={hotel}
                      rooms={searchParams.rooms}
                      travelType={searchParams.travelType}
                      onSelect={handleSelectHotel}
                      small={small}
                    />
                  </Wrapper>
                  <Banners
                    hotelListIndex={index}
                    hotelListLength={paginatedHotels.length}
                    firstInlineAd={firstUpliftAd}
                    secondInlineAd={secondUpliftAd}
                    topZoneAd={topZoneAd}
                    serviceCenterBannerData={serviceCenterBannerData}
                    forceSmallSizeForMapView={small}
                  />
                </Fragment>
              );
            })}
            {hotelCountFiltered > paginatedHotels.length + duplicateCount.now &&
              !splitView && <Footer onClickLoadMore={loadMore} />}
          </>
        );
      })()}
    </Flex>
  );
});

export const List = memo(ListComponent);
